/**
 * useReportsData
 *
 * Data layer for the firm-facing Reports section. All reports read the corporate group-backed admin endpoints:
 *
 *   Timekeepers        → /admin/export-stats (+ front-end goals)
 *   Weekly breakdown   → /admin/export-stats, one call per week, prefetched
 *   Unbilled Matters   → /admin/matter-hours
 *   Ratios             → /admin/matter-timekeeper-hours
 *
 * Every tab reads an admin endpoint. None uses /team/reporting-summary, which
 * is bounded by the caller's reporting hierarchy and so cannot serve a
 * firm-wide admin report.
 *
 * Goals remain browser-local. Matters measure activity dates, not opening dates.
 */

import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useApi } from "@/shared/utils/request";
import { countWeekdays, workdaysInYear } from "@/shared/utils/workdays";
import type {
	GoalProgressData,
	GoalProgressRow,
	GoalsByGroup,
	RatioMatter,
	RatioRow,
	RatiosData,
	UnbilledMatterRow,
	UnbilledMattersData,
} from "../types/reports";
import {
	annualHoursForProfType,
	loadGoals,
	pacePct,
	paceStatus,
	periodProgress,
	saveGoals,
	setGoal,
	targetForPeriod,
	variance,
	withDefaults,
} from "../utils/goals";
import {
	ALL_TITLE_GROUPS,
	normalizeProfType,
	type ProfTitleGroup,
} from "../utils/profTitles";
import {
	exportStatsRangeOptions,
	matterHoursOptions,
	matterTimekeeperHoursOptions,
} from "./adminQueryOptions";
import { useProfessionals } from "./useAdminData";

// ─────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────

function toDateKey(d: Date): string {
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${m}-${day}`;
}

export function monthBounds(asOf: Date): {
	from: Date;
	to: Date;
	label: string;
} {
	const from = new Date(asOf.getFullYear(), asOf.getMonth(), 1);
	const to = new Date(asOf.getFullYear(), asOf.getMonth() + 1, 0);
	const label = from.toLocaleDateString(undefined, {
		month: "long",
		year: "numeric",
	});
	return { from, to, label };
}

export function weekBounds(asOf: Date): {
	from: Date;
	to: Date;
	label: string;
} {
	const day = asOf.getDay();
	// Monday-start, matching the calendar (see docs/superpowers calendar-monday-start).
	const offsetToMonday = day === 0 ? -6 : 1 - day;
	const from = new Date(
		asOf.getFullYear(),
		asOf.getMonth(),
		asOf.getDate() + offsetToMonday,
	);
	const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
	const fmt = (d: Date) =>
		d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
	return { from, to, label: `${fmt(from)} – ${fmt(to)}` };
}

// ─────────────────────────────────────────────
// Goals
// ─────────────────────────────────────────────

export interface UseGoalsResult {
	/** Every group, defaults filled in. */
	goals: GoalsByGroup;
	/** Only what the firm overrode — drives the "default" marker. */
	stored: GoalsByGroup;
	updateGoal: (group: ProfTitleGroup, annualHours: number) => void;
	resetAll: () => void;
}

/** Requirements per title group, seeded from localStorage over the defaults. */
export function useGoals(): UseGoalsResult {
	const [stored, setStored] = useState<GoalsByGroup>(() => loadGoals());

	const goals = useMemo(() => withDefaults(stored), [stored]);

	const updateGoal = useCallback(
		(group: ProfTitleGroup, annualHours: number) => {
			setStored((prev) => {
				const next = setGoal(prev, group, annualHours);
				saveGoals(next);
				return next;
			});
		},
		[],
	);

	const resetAll = useCallback(() => {
		setStored({});
		saveGoals({});
	}, []);

	return { goals, stored, updateGoal, resetAll };
}

// ─────────────────────────────────────────────
// Monthly / Weekly
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// Unbilled Matters
// ─────────────────────────────────────────────

export interface UseUnbilledMattersResult {
	data: UnbilledMattersData | null;
	loading: boolean;
	error: string | null;
}

/** Whole days between a date and today. */
function daysSince(dateKey: string): number {
	const then = new Date(`${dateKey}T00:00:00`);
	const now = new Date();
	return Math.max(0, Math.round((now.getTime() - then.getTime()) / 86_400_000));
}

/**
 * Matters at or under an hours threshold for the period.
 *
 * Reads `/admin/matter-hours`, which is firm-wide and driven from matters_tm,
 * so matters with no time at all are included — the bucket the report exists
 * for. `/team/reporting-summary` could not do either: it is bounded by the
 * caller's reporting hierarchy, and its topMatters only lists matters that
 * already have entries.
 *
 * The endpoint returns every matter and the threshold is applied here, so
 * changing the threshold re-filters instantly without another request.
 */
export function useUnbilledMatters(
	from: Date,
	to: Date,
	threshold: number,
	/** Null means every status; an empty array means none. */
	statuses: string[] | null,
): UseUnbilledMattersResult {
	const { makeApiRequest } = useApi();

	const query = useQuery({
		...matterHoursOptions(toDateKey(from), toDateKey(to), makeApiRequest),
		staleTime: 60_000,
		placeholderData: keepPreviousData,
	});

	const data = useMemo<UnbilledMattersData | null>(() => {
		if (!query.data) return null;
		const all = query.data.matters;

		const statusSet = statuses === null ? null : new Set(statuses);

		const rows: UnbilledMatterRow[] = all
			.filter((m) => m.hours <= threshold)
			.filter((m) => statusSet === null || statusSet.has(m.status ?? "—"))
			.map((m) => ({
				matterId: m.matter_id,
				matterNumber: m.matter_number,
				matterDescription: m.matter_description,
				client: m.client,
				status: m.status,
				hours: m.hours,
				entries: m.entries,
				lifetimeHours: m.lifetime_hours,
				firstActivity: m.first_activity,
				lastActivity: m.last_activity,
				daysStale: m.last_activity ? daysSince(m.last_activity) : null,
			}))
			// Worst first: no time at all, then least time, then most stale.
			.sort(
				(a, b) =>
					a.hours - b.hours ||
					(b.daysStale ?? Infinity) - (a.daysStale ?? Infinity),
			);

		return {
			rows,
			threshold,
			underThresholdCount: rows.length,
			totalMatters: all.length,
			availableStatuses: [...new Set(all.map((m) => m.status ?? "—"))].sort(),
		};
	}, [query.data, threshold, statuses]);

	return {
		data,
		loading: query.isLoading,
		error: query.error?.message ?? null,
	};
}

// ─────────────────────────────────────────────
// Ratios
// ─────────────────────────────────────────────

export interface UseRatiosResult {
	data: RatiosData | null;
	loading: boolean;
	error: string | null;
}

/**
 * Per-timekeeper contribution to each matter.
 *
 * Reads `/admin/matter-timekeeper-hours` — one firm-wide request. The previous
 * implementation fanned out one `/team/reporting-summary` call per timekeeper,
 * which was slow, capped at 40 people, and bounded by the caller's reporting
 * hierarchy so an admin never saw the whole firm.
 *
 * Title and matter filtering happen here so changing either re-filters
 * instantly without another request.
 */
export function useRatios(
	from: Date,
	to: Date,
	/** Null means every title group. */
	profTypeFilter: ProfTitleGroup | null,
	/** Null means every matter; otherwise the matter ids to keep. */
	matterFilter: string[] | null,
): UseRatiosResult {
	const { makeApiRequest } = useApi();

	const query = useQuery({
		...matterTimekeeperHoursOptions(
			toDateKey(from),
			toDateKey(to),
			makeApiRequest,
		),
		staleTime: 60_000,
		placeholderData: keepPreviousData,
	});

	const data = useMemo<RatiosData | null>(() => {
		if (!query.data) return null;
		const raw = query.data.rows;

		const availableTypes = (() => {
			const present = new Set<ProfTitleGroup>();
			for (const r of raw) present.add(normalizeProfType(r.prof_type));
			return ALL_TITLE_GROUPS.filter((g) => present.has(g));
		})();

		// Matter totals span every timekeeper, so a share stays honest even when
		// the view is filtered down to partners.
		const matterTotals = new Map<string, number>();
		for (const r of raw) {
			matterTotals.set(
				r.matter_id,
				(matterTotals.get(r.matter_id) ?? 0) + r.hours,
			);
		}

		const matterSet = matterFilter === null ? null : new Set(matterFilter);
		const kept = raw.filter((r) => {
			if (matterSet !== null && !matterSet.has(r.matter_id)) return false;
			if (
				profTypeFilter !== null &&
				normalizeProfType(r.prof_type) !== profTypeFilter
			) {
				return false;
			}
			return true;
		});

		const rows: RatioRow[] = kept.map((r) => {
			const total = matterTotals.get(r.matter_id) ?? 0;
			return {
				profId: r.prof_id,
				profName: r.prof_name,
				profType: normalizeProfType(r.prof_type),
				matterId: r.matter_id,
				matterNumber: r.matter_number,
				matterDescription: r.matter_description,
				client: r.client,
				hours: r.hours,
				sharePct: total === 0 ? 0 : Math.round((r.hours / total) * 100),
			};
		});
		rows.sort((a, b) => b.hours - a.hours);

		// Regroup into matters for the chart.
		const byMatter = new Map<string, RatioMatter>();
		for (const r of rows) {
			let m = byMatter.get(r.matterId);
			if (!m) {
				m = {
					matterId: r.matterId,
					matterNumber: r.matterNumber,
					matterDescription: r.matterDescription,
					client: r.client,
					totalHours: 0,
					contributors: [],
				};
				byMatter.set(r.matterId, m);
			}
			m.totalHours += r.hours;
			m.contributors.push({
				profId: r.profId,
				profName: r.profName,
				hours: r.hours,
				sharePct: r.sharePct,
			});
		}
		const matters = [...byMatter.values()].sort(
			(a, b) => b.totalHours - a.totalHours,
		);
		for (const m of matters) m.contributors.sort((a, b) => b.hours - a.hours);

		return {
			rows,
			matters,
			availableTypes,
			totalMatters: matterTotals.size,
		};
	}, [query.data, profTypeFilter, matterFilter]);

	return {
		data,
		loading: query.isLoading,
		error: query.error?.message ?? null,
	};
}

// ─────────────────────────────────────────────
// Timekeepers report (period totals)
// ─────────────────────────────────────────────

export interface MonthlyRunParams {
	from: Date;
	to: Date;
	/** Empty means every timekeeper. */
	profIds: string[];
}

export interface UseMonthlyReportResult {
	data: GoalProgressData | null;
	loading: boolean;
	error: string | null;
}

/**
 * Hours vs goal per timekeeper for an explicit date range.
 *
 * Reads `/admin/export-stats`, which is firm-wide by construction: it joins
 * entry groups to professionals with no reporting-hierarchy check, so an admin
 * sees every licensed timekeeper.
 *
 * NOT `/team/reporting-summary`: that endpoint bounds the caller to their
 * `reports_to` descendants plus delegates, and asking for anyone outside that
 * set returns 403. Selection is applied in the browser, so no id the caller
 * cannot see is ever sent.
 */
export function useMonthlyReport(
	params: MonthlyRunParams | null,
	goals: GoalsByGroup,
): UseMonthlyReportResult {
	const { makeApiRequest } = useApi();

	const query = useQuery({
		...exportStatsRangeOptions(
			params ? toDateKey(params.from) : "",
			params ? toDateKey(params.to) : "",
			makeApiRequest,
		),
		enabled: params !== null,
	});

	const data = useMemo<GoalProgressData | null>(() => {
		if (!params || !query.data) return null;

		const { elapsed, total, fraction } = periodProgress(
			params.from,
			params.to,
			new Date(),
		);
		const yearDays = workdaysInYear(params.to.getFullYear());
		const periodDays = countWeekdays(params.from, params.to);

		const selected = new Set(params.profIds);
		const attorneys =
			selected.size > 0
				? query.data.attorneys.filter((a) => selected.has(a.prof_id))
				: query.data.attorneys;

		const rows: GoalProgressRow[] = attorneys.map((a) => {
			const target = targetForPeriod(
				annualHoursForProfType(goals, a.prof_type),
				periodDays,
				yearDays,
			);
			const pct = pacePct(a.total_hours, target, fraction);
			return {
				profId: a.prof_id,
				profName: a.name,
				hours: a.total_hours,
				target,
				pacePct: pct,
				variance: variance(a.total_hours, target, fraction),
				status: paceStatus(pct),
				priorPeriod: null,
			};
		});

		rows.sort((a, b) => b.hours - a.hours);

		const totalHours = rows.reduce((sum, r) => sum + r.hours, 0);
		const totalTarget = rows.reduce((sum, r) => sum + (r.target ?? 0), 0);
		const fmt = (d: Date) =>
			d.toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
			});

		return {
			rows,
			totals: {
				hours: totalHours,
				target: totalTarget,
				pacePct: pacePct(totalHours, totalTarget, fraction),
				onTrackCount: rows.filter(
					(r) => r.status === "ahead" || r.status === "on",
				).length,
				timekeeperCount: rows.length,
				elapsedDays: elapsed,
				totalDays: total,
			},
			periodLabel: `${fmt(params.from)} – ${fmt(params.to)}`,
		};
	}, [params, query.data, goals]);

	return {
		data,
		loading: params !== null && query.isLoading,
		error: query.error?.message ?? null,
	};
}

/**
 * Timekeeper options for the picker.
 *
 * Every licensed professional, from the admin roster. Safe to list in full
 * because filtering happens in the browser rather than by passing ids to a
 * hierarchy-scoped endpoint.
 */
export function useTimekeeperOptions(): {
	options: Array<{ value: string; label: string; sublabel?: string }>;
	loading: boolean;
} {
	const { professionals, loading } = useProfessionals({ activeOnly: true });

	const options = useMemo(
		() =>
			professionals
				.map((p) => ({
					value: p.prof_id,
					label: p.full_name,
					sublabel: p.email,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[professionals],
	);

	return { options, loading };
}

// ─────────────────────────────────────────────
// Weekly breakdown (prefetched)
// ─────────────────────────────────────────────

/**
 * Cap on how many weeks are fetched up front. A month is 5-6; beyond a quarter
 * the prefetch stops paying for itself and rows fetch lazily instead.
 */
const MAX_PREFETCHED_WEEKS = 14;

export interface WeekBucket {
	weekStart: Date;
	weekEnd: Date;
	label: string;
	/** Business days in this bucket, for pro-rating the goal. */
	workdays: number;
}

/** Monday-start weeks covering the range, clipped to its edges. */
function weeksInRange(from: Date, to: Date): Array<{ start: Date; end: Date }> {
	const out: Array<{ start: Date; end: Date }> = [];
	const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
	// Walk back to Monday so buckets line up with calendar weeks.
	const day = cursor.getDay();
	cursor.setDate(cursor.getDate() + (day === 0 ? -6 : 1 - day));

	while (cursor <= to) {
		const weekEnd = new Date(cursor);
		weekEnd.setDate(weekEnd.getDate() + 6);
		out.push({
			start: cursor < from ? new Date(from) : new Date(cursor),
			end: weekEnd > to ? new Date(to) : new Date(weekEnd),
		});
		cursor.setDate(cursor.getDate() + 7);
	}
	return out;
}

export interface UseWeeklyBreakdownResult {
	/** Every week in the range, in order — including ones with no activity. */
	weeks: WeekBucket[];
	/** prof_id → week index → hours. Absent means the week had none. */
	hoursByProf: Map<string, Map<number, number>>;
	loading: boolean;
	/** True when the range was too long to prefetch every week. */
	truncated: boolean;
}

export function useWeeklyBreakdown(
	from: Date,
	to: Date,
	enabled: boolean,
): UseWeeklyBreakdownResult {
	const { makeApiRequest } = useApi();

	const allWeeks = useMemo(() => weeksInRange(from, to), [from, to]);
	const truncated = allWeeks.length > MAX_PREFETCHED_WEEKS;
	const fetched = useMemo(
		() => allWeeks.slice(0, MAX_PREFETCHED_WEEKS),
		[allWeeks],
	);

	const results = useQueries({
		queries: fetched.map((w) => ({
			...exportStatsRangeOptions(
				toDateKey(w.start),
				toDateKey(w.end),
				makeApiRequest,
			),
			enabled,
			// The past does not change; hold these so re-renders and repeated
			// expansions never refetch.
			staleTime: 5 * 60_000,
			gcTime: 30 * 60_000,
		})),
	});

	const weeks = useMemo<WeekBucket[]>(() => {
		const fmt = (d: Date) =>
			d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
		return fetched.map((w) => ({
			weekStart: w.start,
			weekEnd: w.end,
			label: `${fmt(w.start)} – ${fmt(w.end)}`,
			workdays: countWeekdays(w.start, w.end),
		}));
	}, [fetched]);

	/**
	 * The endpoint's `HAVING SUM(t.units) > 0` drops anyone with no time that
	 * week, so a missing entry means zero rather than unknown. Consumers walk
	 * `weeks` and default to 0, which is why the grid is returned separately —
	 * otherwise a week someone billed nothing would silently disappear from
	 * their series instead of showing as the zero it is.
	 */
	const hoursByProf = useMemo(() => {
		const map = new Map<string, Map<number, number>>();
		results.forEach((res, i) => {
			if (!res.data) return;
			for (const a of res.data.attorneys) {
				let inner = map.get(a.prof_id);
				if (!inner) {
					inner = new Map<number, number>();
					map.set(a.prof_id, inner);
				}
				inner.set(i, a.total_hours);
			}
		});
		return map;
	}, [results]);

	return {
		weeks,
		hoursByProf,
		loading: enabled && results.some((r) => r.isLoading),
		truncated,
	};
}
